import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { FormElement, Page } from "./types";
import { SortableFormElement } from "./FormElementTypes/SortableFormElement";
import { StripeSettings } from "./FormElementTypes/StripeSettings";
interface FormElementsProps {
  elements: FormElement[];
  onChange: (elements: FormElement[]) => void;
  afterSubmit?: {
    stripeEnabled?: boolean;
    stripePaymentLink?: string;
    stripeSuccessUrl?: string;
    stripeCancelUrl?: string;
  };
  onAfterSubmitChange?: (settings: {
    stripeEnabled?: boolean;
    stripePaymentLink?: string;
    stripeSuccessUrl?: string;
    stripeCancelUrl?: string;
  }) => void;
  pages?: Page[];
  onFinishPageChange?: (pageId: string) => void;
  finishPageId?: string;
  siteTitle?: string;
  onSiteTitleChange?: (title: string) => void;
}
export const FormElements = ({
  elements,
  onChange,
  afterSubmit,
  onAfterSubmitChange,
  pages,
  onFinishPageChange,
  finishPageId,
  siteTitle = "",
  onSiteTitleChange
}: FormElementsProps) => {
  const MAX_ELEMENTS = 5;
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates
  }));
  const addElement = () => {
    if (elements.length >= MAX_ELEMENTS) {
      return;
    }
    onChange([...elements, {
      id: crypto.randomUUID(),
      type: "text",
      title: "",
      required: false
    }]);
  };
  const updateElement = (index: number, updates: Partial<FormElement>) => {
    const newElements = [...elements];
    newElements[index] = {
      ...newElements[index],
      ...updates
    };
    onChange(newElements);
  };
  const removeElement = (index: number) => {
    onChange(elements.filter((_, i) => i !== index));
  };
  const handleDragEnd = (event: any) => {
    const {
      active,
      over
    } = event;
    if (active.id !== over.id) {
      const oldIndex = elements.findIndex(item => item.id === active.id);
      const newIndex = elements.findIndex(item => item.id === over.id);
      onChange(arrayMove(elements, oldIndex, newIndex));
    }
  };
  return <div className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="formTitle">Funnel Title</Label>
          <Input id="formTitle" value={siteTitle} onChange={e => onSiteTitleChange?.(e.target.value)} placeholder="Welcome to My Funnel" className="bg-white" />
          <p className="text-sm text-muted-foreground">
            This title will be displayed at the top of your form
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-slate-800">Űrlap mezők</h3>
            <p className="text-sm text-[#8E9196]">
              {elements.length}/{MAX_ELEMENTS} fields in use
            </p>
          </div>
          <Button onClick={addElement} size="sm" disabled={elements.length >= MAX_ELEMENTS} className="bg-primary hover:bg-primary/90 text-white rounded-full">
            <Plus className="w-4 h-4 mr-2" />
            Add Field
          </Button>
        </div>
      </div>

      {pages && pages.length > 0 && <div className="space-y-2">
          <Label>Success Page</Label>
          <Select value={finishPageId} onValueChange={value => onFinishPageChange?.(value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a page" />
            </SelectTrigger>
            <SelectContent>
              {pages.map(page => <SelectItem key={page.id} value={page.id}>
                  {page.title}
                </SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">Ezt az oldalt fogjuk megjeleníteni az űrlap elküldése után.</p>
        </div>}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={elements.map(e => e.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {elements.map((element, index) => <SortableFormElement key={element.id} element={element} index={index} onUpdate={updates => updateElement(index, updates)} onRemove={() => removeElement(index)} />)}
          </div>
        </SortableContext>
      </DndContext>

      {onAfterSubmitChange && afterSubmit && <StripeSettings afterSubmit={afterSubmit} onAfterSubmitChange={onAfterSubmitChange} />}
    </div>;
};